import React, { useCallback, useEffect, useRef, useState } from "react";
import { Play, Pause } from "lucide-react";
import { getMedia } from "../data/mediaRegistry";

// Single active card video at a time (module scope).
let activeCardVideo = null;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const savesData = () =>
  typeof navigator !== "undefined" && navigator.connection && navigator.connection.saveData === true;

/**
 * Premium inline motion media. No native controls, no YouTube framing.
 * - mode="card": pointer-enter plays (muted, playsInline), pointer-leave
 *   pauses + resets to poster. Keyboard focus reveals an accessible
 *   Play/Pause button; touch gets a persistent overlay button. Navigation
 *   links are the PARENT's responsibility (never hover-only info).
 * - mode="hero": autoplays silently when visible, discreet pause/play.
 * - prefers-reduced-motion / saveData → poster + explicit play only.
 * - Pauses offscreen & on document hide; lazy src attach; preload=none;
 *   fixed aspect wrapper prevents CLS; play() rejection → poster fallback.
 */
export default function MotionMedia({
  mediaKey,
  alt = "",
  mode = "card",
  aspectClass = "aspect-[4/3]",
  className = "",
  imgClassName = "",
  testid = "motion-media",
  posterOverride = null,
  eager = false,
  fit = "cover",
  toggleClass = null,
}) {
  const entry = getMedia(mediaKey) || {};
  const video = entry.video || null;
  const preview = entry.preview || null;
  // Cards use the lightweight preview; hero/detail use the full film. On
  // constrained/small screens heroes use a dedicated 720p mobile encode when
  // available (falls back to the card preview) to save data without softness.
  const [smallScreen] = useState(
    () => typeof window !== "undefined" && window.matchMedia && window.matchMedia("(max-width: " + (entry.mobileBreakpoint || 767) + "px)").matches
  );
  const poster = posterOverride || (smallScreen && entry.mobilePoster) || entry.poster || null;
  const src = mode === "card" ? preview || video : (smallScreen && (entry.mobileHero || preview)) || video;

  const wrapRef = useRef(null);
  const videoRef = useRef(null);
  const [attached, setAttached] = useState(false);
  const [wantPlay, setWantPlay] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [hasFrame, setHasFrame] = useState(false);
  const [failed, setFailed] = useState(false);
  const [focused, setFocused] = useState(false);
  const [isTouch, setIsTouch] = useState(false);
  // Explicit playback: once the user presses the play/pause control (pointer
  // or keyboard), hover/auto behaviours stop overriding their choice.
  const [explicit, setExplicit] = useState(false);
  const manualOnly = prefersReducedMotion() || savesData();

  const doPause = useCallback((reset) => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    if (reset) {
      try { v.currentTime = 0; } catch { /* noop */ }
    }
    setPlaying(false);
  }, []);

  // Drive playback from attached + wantPlay
  useEffect(() => {
    if (!attached || !video) return;
    const v = videoRef.current;
    if (!v) return;
    if (wantPlay && !failed) {
      if (mode === "card" && activeCardVideo && activeCardVideo !== v) {
        try { activeCardVideo.pause(); } catch { /* noop */ }
      }
      if (mode === "card") activeCardVideo = v;
      const p = v.play();
      if (p && p.catch) {
        p.then(() => setPlaying(true)).catch(() => { setFailed(true); setPlaying(false); });
      } else {
        setPlaying(true);
      }
    } else {
      doPause(mode === "card");
    }
  }, [attached, wantPlay, video, failed, mode, doPause]);

  // Hero: autoplay when visible. All modes: pause offscreen / document hidden.
  useEffect(() => {
    if (!video) return undefined;
    const el = wrapRef.current;
    if (!el || !("IntersectionObserver" in window)) return undefined;
    const io = new IntersectionObserver(
      ([en]) => {
        if (en.isIntersecting) {
          if (mode === "hero" && !manualOnly && !failed && !explicit) {
            setAttached(true);
            setWantPlay(true);
          }
        } else {
          setWantPlay(false);
        }
      },
      { threshold: 0.2 }
    );
    io.observe(el);
    const onVis = () => { if (document.hidden) setWantPlay(false); };
    document.addEventListener("visibilitychange", onVis);
    return () => { io.disconnect(); document.removeEventListener("visibilitychange", onVis); };
  }, [video, mode, manualOnly, failed, explicit]);

  const onPointerEnter = (e) => {
    if (mode !== "card" || !video || manualOnly || failed || explicit) return;
    if (e.pointerType && e.pointerType !== "mouse") return;
    setAttached(true);
    setWantPlay(true);
  };
  const onPointerLeave = (e) => {
    if (mode !== "card" || !video || explicit) return;
    if (e.pointerType && e.pointerType !== "mouse") return;
    setWantPlay(false);
  };
  const onTouchStart = () => setIsTouch(true);

  // Hero toggle sits top-right within the hero, safely below the baked
  // wordmark and clear of hero CTAs / floating strips. Card controls unchanged.
  const togglePos = toggleClass || (mode === "hero" ? "top-[72px] right-5" : "bottom-2 right-2");
  const fitClass = fit === "contain" ? "object-contain" : "object-cover";
  const toggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!video || failed) return;
    setAttached(true);
    if (!explicit) {
      setExplicit(true);
      // Cards: a hover-start followed by an immediate click used to cancel
      // playback — the first explicit interaction on a card always plays.
      // Heroes (visibly playing) keep true toggle semantics.
      if (mode === "card") setWantPlay(true);
      else setWantPlay((w) => !w);
    } else {
      setWantPlay((w) => !w);
    }
  };

  const showButton = Boolean(video) && !failed && (mode === "hero" || focused || isTouch || manualOnly);

  return (
    <div
      ref={wrapRef}
      data-testid={testid}
      className={`relative overflow-hidden ${aspectClass} ${className}`}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onTouchStart={onTouchStart}
      onFocus={() => setFocused(true)}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setFocused(false); }}
    >
      {poster && (
        <img
          src={poster}
          alt={alt}
          loading={eager ? "eager" : "lazy"}
          fetchPriority={eager ? "high" : "auto"}
          decoding="async"
          className={`absolute inset-0 w-full h-full ${fitClass} ${imgClassName}`}
        />
      )}
      {video && attached && !failed && (
        <video
          ref={videoRef}
          src={src}
          muted
          loop
          playsInline
          preload="none"
          aria-hidden="true"
          tabIndex={-1}
          onError={() => { setFailed(true); setPlaying(false); }}
          onPlaying={() => { setHasFrame(true); setPlaying(true); }}
          onPause={() => setPlaying(false)}
          className={`absolute inset-0 w-full h-full ${fitClass} transition-opacity duration-[250ms] ${(mode === "card" ? playing : hasFrame) ? "opacity-100" : "opacity-0"}`}
        />
      )}
      {/* Brand wordmark + captions are baked into the final video files by
          the owner — no overlays here. The discreet "Design visualization"
          label is rendered by parents OUTSIDE the media / in figcaptions. */}
      {showButton && (
        <button
          type="button"
          onClick={toggle}
          aria-label={playing ? "Pause video" : "Play video"}
          aria-pressed={playing}
          data-testid={`${testid}-toggle`}
          className={`absolute ${togglePos} z-20 inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#FAF5EC]/90 text-[#6E1F2C] border border-[#6E1F2C]/25 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A963] transition-colors`}
        >
          {playing ? <Pause size={14} /> : <Play size={14} className="ml-0.5" />}
        </button>
      )}
      {/* Keyboard users: an always-tabbable toggle (visually revealed on focus) */}
      {Boolean(video) && !failed && !showButton && (
        <button
          type="button"
          onClick={toggle}
          onFocus={() => setFocused(true)}
          aria-label="Play video"
          data-testid={`${testid}-kb-toggle`}
          className={`absolute ${togglePos} z-20 w-11 h-11 rounded-full opacity-0 focus:opacity-100 focus-visible:ring-2 focus-visible:ring-[#D4A963] bg-[#FAF5EC]/90 text-[#6E1F2C] border border-[#6E1F2C]/25 inline-flex items-center justify-center`}
        >
          <Play size={14} className="ml-0.5" />
        </button>
      )}
    </div>
  );
}
